import { Braces, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { failReportJob, trackReportJob } from "@/hooks/inboxView/useInboxViewReportJobs";
import { useExportInboxViewPdf } from "@/hooks/inboxView/useInboxViews";
import type { InboxView, InboxViewColumn, InboxViewRow } from "@/types/inboxView/inboxView";
import { exportViewToCsv } from "./exportCsv";
import { exportViewToJson } from "./exportJson";

interface ViewDownloadMenuProps {
  view: InboxView;
  viewName: string;
  columns: InboxViewColumn[];
  /** The rows on screen, in their on-screen order. */
  rows: InboxViewRow[];
}

export const ViewDownloadMenu = ({ view, viewName, columns, rows }: ViewDownloadMenuProps) => {
  const exportPdf = useExportInboxViewPdf();

  const downloadPdf = () => {
    const requestId = crypto.randomUUID();
    trackReportJob(
      requestId,
      toast.loading("Building your PDF report. You can keep working, it will download when it's ready."),
    );
    exportPdf.mutate(
      { viewId: view.id, rowIds: rows.flatMap((row) => (row.id ? [row.id] : [])), requestId },
      { onError: () => failReportJob(requestId, "We couldn't build the PDF report. Please try again.") },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={columns.length === 0 || rows.length === 0 || exportPdf.isPending}
          className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl text-sm font-medium border border-[#E5EAF0] bg-white text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary transition-colors disabled:opacity-50"
          title="Download this view"
          aria-label="Download this view"
        >
          {exportPdf.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          <span className="hidden sm:inline">Download</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => exportViewToCsv(viewName, columns, rows)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          CSV spreadsheet
        </DropdownMenuItem>
        <DropdownMenuItem onClick={downloadPdf}>
          <FileText className="h-4 w-4 mr-2" />
          PDF report
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportViewToJson(view, viewName, columns, rows)}>
          <Braces className="h-4 w-4 mr-2" />
          JSON data
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
